import React from 'react';
import { useNavigate } from 'react-router-dom';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <div className="bg-white rounded-[24px] shadow-md p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-sm font-medium">{title}</p>
        <p className="text-2xl font-bold mt-2">{value}</p>
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        {icon}
      </div>
    </div>
  </div>
);

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();

  // Mock data - replace with actual data from your backend
  const stats = {
    totalTests: 12,
    completedTests: 8,
    averageScore: 75,
    streakDays: 5,
  };

  const recentActivities = [
    { id: 1, title: 'Agriculture Quiz', score: 85, date: '2024-03-20' },
    { id: 2, title: 'Soil Science Test', score: 78, date: '2024-03-19' },
    { id: 3, title: 'Plant Pathology', score: 92, date: '2024-03-18' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Tests"
            value={stats.totalTests}
            icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>}
            color="bg-blue-500"
          />
          <StatCard
            title="Completed Tests"
            value={stats.completedTests}
            icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            color="bg-green-500"
          />
          <StatCard
            title="Average Score"
            value={`${stats.averageScore}%`}
            icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
            color="bg-purple-500"
          />
          <StatCard
            title="Streak Days"
            value={stats.streakDays}
            icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            color="bg-orange-500"
          />
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-[24px] shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-[24px]">
                <div>
                  <h3 className="font-medium text-gray-900">{activity.title}</h3>
                  <p className="text-sm text-gray-500">{activity.date}</p>
                </div>
                <div className="flex items-center">
                  <span className="text-lg font-semibold text-green-600">{activity.score}%</span>
                  <button
                    onClick={() => navigate(`/test-detail/${activity.id}`)}
                    className="ml-4 text-blue-600 hover:text-blue-800"
                    aria-label="View Details"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            onClick={() => navigate('/test-series')}
            className="p-6 bg-white rounded-[24px] shadow-md hover:shadow-lg transition-shadow"
            aria-label="Start New Test"
          >
            <h3 className="text-lg font-semibold text-gray-900">Start New Test</h3>
            <p className="text-gray-500 mt-2">Begin a new test series or quiz</p>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="p-6 bg-white rounded-[24px] shadow-md hover:shadow-lg transition-shadow"
            aria-label="View Profile"
          >
            <h3 className="text-lg font-semibold text-gray-900">View Profile</h3>
            <p className="text-gray-500 mt-2">Check your detailed progress and achievements</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage; 